<script setup lang="ts">
/**
 * A small status dot, optionally with a visible label (`showLabel` + default
 * slot). `hue` colours it with a category hue instead of the variant (a mail
 * tag, a calendar) — then it is a plain solid dot. `label` names a dot that
 * stands alone (no visible text next to it) for screen readers and as its
 * tooltip; without it the dot is decorative.
 */
import { computed } from 'vue';
import { hueVars, type NxHue } from '../lib/hue';

export type NxStatusDotVariant = 'idle' | 'active' | 'review' | 'success' | 'warning' | 'danger';

const props = withDefaults(
  defineProps<{
    variant?: NxStatusDotVariant;
    showLabel?: boolean;
    /** Category hue instead of the variant colour. */
    hue?: NxHue;
    /** Accessible name and tooltip of a dot without visible text. */
    label?: string;
  }>(),
  { variant: 'idle', showLabel: false, hue: undefined, label: undefined }
);

const hueStyle = computed(() => hueVars(props.hue));
const named = computed(() => !!props.label && !props.showLabel);
</script>

<template>
  <div
    class="nx-status-dot-wrap"
    :role="named ? 'img' : undefined"
    :aria-label="named ? props.label : undefined"
    :title="named ? props.label : undefined"
  >
    <span
      class="nx-status-dot"
      :class="[`variant-${props.variant}`, { 'has-hue': !!props.hue }]"
      :style="hueStyle"
      :aria-hidden="named || props.showLabel ? 'true' : undefined"
    />
    <span v-if="showLabel" class="nx-status-dot-label">
      <slot />
    </span>
  </div>
</template>

<style scoped>
/* NxStatusDot consumes the --nxt-status-dot-bg component token (I-596); each
   colored variant re-points it at the matching semantic role's solid step. */
.nx-status-dot-wrap {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}

.nx-status-dot {
  width: 0.75em;
  height: 0.75em;
  border-radius: var(--nxt-radius-full);
  flex-shrink: 0;
  background: var(--nxt-status-dot-bg);
}

.nx-status-dot.variant-idle {
  --nxt-status-dot-bg: transparent;
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 0.5) var(--nxt-border-strong);
}

.nx-status-dot.variant-active {
  --nxt-status-dot-bg: var(--nxt-brand-bg);
  box-shadow:
    0 0 calc(var(--nxt-hairline) * 3) var(--nxt-hairline)
      color-mix(in srgb, var(--nxt-brand-bg) 50%, var(--nxt-brand-subtle-bg)),
    0 0 0 calc(var(--nxt-size-unit) * 0.1875) var(--nxt-brand-subtle-bg);
}

.nx-status-dot.variant-review {
  --nxt-status-dot-bg: var(--nxt-info-bg);
}

.nx-status-dot.variant-success {
  --nxt-status-dot-bg: var(--nxt-success-bg);
}

.nx-status-dot.variant-warning {
  --nxt-status-dot-bg: var(--nxt-warning-bg);
}

.nx-status-dot.variant-danger {
  --nxt-status-dot-bg: var(--nxt-danger-bg);
}

/* A hue wins over every variant: a plain solid dot in the category colour. */
.nx-status-dot.has-hue {
  --nxt-status-dot-bg: var(--hue);
  border: 0;
  box-shadow: none;
}

@media (forced-colors: active) {
  .nx-status-dot {
    border: var(--nxt-hairline) solid CanvasText;
  }
}
</style>
